import React, { useState, useEffect, useRef, useMemo } from "react";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { useGetCompanyMembersQuery } from "@/features/Members/api";
import WorkloadSummaryDrawer from "./WorkloadSummaryDrawer";
import {
  useGetListOfBugsQuery,
  useUpdateBugDetailMutation,
  useLazyGetBugDetailByBugIdQuery,
  useGetListOfStatusQuery,
  useGetSprintByIdForWorkloadQuery,
} from "@/features/bughurd/api";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import HighestIcon from "@/assets/icons/highest.svg?react";
import HighIcon from "@/assets/icons/high.svg?react";
import MediumIcon from "@/assets/icons/medium.svg?react";
import LowIcon from "@/assets/icons/low.svg?react";
import EditPopover from "../../EditPopover";
import { useAppSelector } from "@/app/hooks";
import { Sprint, ViewMode, DayTask } from "./types";
import { startOfMonth, addDays, getDaysInMonth } from "./dateUtils";
import { SprintHeader } from "./SprintHeader";
import { WorkloadGrid } from "./WorkloadGrid";
import { UnassignedTasksSection } from "./UnassignedTasksSection";
import { createColumns } from "./dataGridColumns";
import TaskUpdateDrawer from "@/pages/Board/bugherdComponents/TaskUpdateDrawer";

// Helper function to build users data from new API structure
const buildUsersFromNewData = (
  bugsData: any,
  sprint: any,
  viewStart: Date,
  viewEnd: Date,
  viewMode: string,
  viewDays: Date[]
) => {
  const items = bugsData?.items || [];
  const users: any[] = [];
  const unassignedUnscheduledTasks: any[] = [];

  items.forEach((assigneeData: any) => {
    const assignee = assigneeData._id;
    const workloadByDate = assigneeData.workloadByDate || [];

    // ✅ UPDATED: Check if assignee exists (not null/undefined)
    const hasAssignee = assignee && assignee._id;

    // Initialize user object only if assignee exists
    if (hasAssignee) {
      const user = {
        id: assignee._id,
        name: assignee.fullName || assignee.email,
        email: assignee.email,
        avatar: null,
        tasks: [] as DayTask[],
        tasksByDate: {} as Record<string, DayTask[]>,
      };

      // Pre-initialize all dates in the view range with empty arrays
      viewDays.forEach((day) => {
        const dateKey = day.toISOString().split("T")[0];
        user.tasksByDate[dateKey] = [];
      });

      // Process each date entry
      workloadByDate.forEach((dateEntry: any) => {
        const { dueDate, issues } = dateEntry;

        issues.forEach((issue: any) => {
          // ✅ UPDATED: A task is unassigned/unscheduled if it's missing EITHER assignee OR due date
          const hasDueDate = dueDate && dueDate !== "No Due Date";

          // If task doesn't have BOTH assignee AND due date, add to unassigned section
          if (!hasAssignee || !hasDueDate) {
            unassignedUnscheduledTasks.push({
              id: issue._id,
              name: issue.summary || "Untitled Task",
              bugKey: issue.bugKey,
              severity: issue.severity,
              status: issue.bugStatus,
              assignee: hasAssignee ? assignee : null,
              dueDate: hasDueDate ? dueDate : null,
              estimation: issue.estimation,
              type: issue.type,
              project: issue.project,
              numericEstimation: issue.numericEstimation || 0,
            });
            return; // Don't add to workload grid
          }

          // Parse the due date
          const taskDate = new Date(dueDate);
          taskDate.setHours(0, 0, 0, 0);
          const dateKey = taskDate.toISOString().split("T")[0];

          // Check if task is within view range
          if (taskDate >= viewStart && taskDate <= viewEnd) {
            const taskObj = {
              id: issue._id,
              date: dateKey,
              name: issue.summary || "Untitled Task",
              bugKey: issue.bugKey,
              severity: issue.severity,
              status: issue.bugStatus,
              estimation: issue.estimation,
              type: issue.type,
              project: issue.project,
              numericEstimation: issue.numericEstimation || 0,
            };

            // Add to tasks array
            user.tasks.push(taskObj);

            // Add to tasksByDate map for WorkloadGrid
            if (!user.tasksByDate[dateKey]) {
              user.tasksByDate[dateKey] = [];
            }
            user.tasksByDate[dateKey].push(taskObj);
          }
        });
      });

      // Only add user if they have tasks in the view range
      if (user.tasks.length > 0) {
        users.push(user);
      }
    } else {
      // ✅ ADDED: Handle tasks with no assignee at all
      workloadByDate.forEach((dateEntry: any) => {
        const { dueDate, issues } = dateEntry;

        issues.forEach((issue: any) => {
          const hasDueDate = dueDate && dueDate !== "No Due Date";

          // Task has no assignee, so it goes to unassigned section regardless of due date
          unassignedUnscheduledTasks.push({
            id: issue._id,
            name: issue.summary || "Untitled Task",
            bugKey: issue.bugKey,
            severity: issue.severity,
            status: issue.bugStatus,
            assignee: null,
            dueDate: hasDueDate ? dueDate : null,
            estimation: issue.estimation,
            type: issue.type,
            project: issue.project,
            numericEstimation: issue.numericEstimation || 0,
          });
        });
      });
    }
  });

  return { users, unassignedUnscheduledTasks };
};

export const SprintWorkload: React.FC<{
  sprintId?: Sprint;
  projectId: string;
  sprint?: any;
  parentData?: any;
}> = ({ sprintId, projectId, sprint, parentData }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [viewMode, setViewMode] = useState<ViewMode>("sprint");
  const zoomed = Math.min(1.4, Math.max(0.8, zoom));
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();
  const { dateFormat } = useAppSelector((state) => state.appGlobal);
  const [summaryDrawerOpen, setSummaryDrawerOpen] = useState(false);
  const [selectedDateForDrawer, setSelectedDateForDrawer] =
    useState<Date | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [selectedAssignees, setSelectedAssignees] = useState([]);

  const [editingNameId, setEditingNameId] = useState(null);
  const [editedName, setEditedName] = useState("");
  const nameInputRef = useRef(null);
  const boardState = useAppSelector((state) => state.board);
  const { data: companyMemberDataQuery, isLoading: isMembersLoading } =
    useGetCompanyMembersQuery(1);
  const [optimisticUpdates, setOptimisticUpdates] = useState<
    Record<string, any>
  >({});

  const { data: sprintData, isLoading: isSprintLoading } =
    useGetSprintByIdForWorkloadQuery({ sprintId }, { skip: !sprintId });
  const {
    data: bugsDataQuery,
    isLoading: isBugsLoading,
    refetch: refetchBugs,
  } = useGetListOfBugsQuery(
    {
      id: projectId,
      forBoard: true,
      sprint: sprintId,
      viewType: "WORKLOAD",
      page: 1,
      limit: 10,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const { data: statusLists, isLoading: isLoadingStatusLabel } =
    useGetListOfStatusQuery(
      { projectId },
      {
        skip: !projectId,
      }
    );

  const [updateBug, { isLoading: updatingBug }] = useUpdateBugDetailMutation();
  const handleDateHeaderClick = (date: Date) => {
    setSelectedDate(date);
    setSummaryDrawerOpen(true);
    setSelectedDateForDrawer(date);
  };
  useEffect(() => {
    if (!isBugsLoading && !isFetchingEditingBug) {
      setOptimisticUpdates({});
    }
  }, [bugsDataQuery]);
  const handleClose = () => {
    setIsModalOpen(false);
    setSelectedTaskId(null);
  };

  const [
    fetchBugDetail,
    { data: editingBugDetail, isFetching: isFetchingEditingBug },
  ] = useLazyGetBugDetailByBugIdQuery();

  // Calculate view dates based on sprint dates and view mode
  const { anchor, viewStart, viewEnd, viewDays } = useMemo(() => {
    if (viewMode === "sprint") {
      // Sprint Date View - Show full sprint duration
      if (sprint?.startDate && sprint?.endDate) {
        const sprintStart = new Date(sprint?.startDate);
        const sprintEnd = new Date(sprint?.endDate);

        sprintStart.setHours(0, 0, 0, 0);
        sprintEnd.setHours(0, 0, 0, 0);

        const daysDiff =
          Math.floor(
            (sprintEnd.getTime() - sprintStart.getTime()) /
              (1000 * 60 * 60 * 24)
          ) + 1;

        return {
          anchor: sprintStart,
          viewStart: sprintStart,
          viewEnd: sprintEnd,
          viewDays: Array.from({ length: daysDiff }, (_, i) =>
            addDays(sprintStart, i)
          ),
        };
      }
    } else {
      // Month View - Show full month based on sprint start date or current date
      const baseDate = sprint?.startDate
        ? new Date(sprint.startDate)
        : new Date();
      const monthStart = startOfMonth(baseDate);
      const daysInMonth = getDaysInMonth(monthStart);

      return {
        anchor: monthStart,
        viewStart: monthStart,
        viewEnd: new Date(
          monthStart.getFullYear(),
          monthStart.getMonth(),
          daysInMonth
        ),
        viewDays: Array.from(
          { length: daysInMonth },
          (_, i) =>
            new Date(monthStart.getFullYear(), monthStart.getMonth(), i + 1)
        ),
      };
    }

    // Fallback
    const baseDate = new Date();
    const monthStart = startOfMonth(baseDate);
    const daysInMonth = getDaysInMonth(monthStart);

    return {
      anchor: monthStart,
      viewStart: monthStart,
      viewEnd: new Date(
        monthStart.getFullYear(),
        monthStart.getMonth(),
        daysInMonth
      ),
      viewDays: Array.from(
        { length: daysInMonth },
        (_, i) =>
          new Date(monthStart.getFullYear(), monthStart.getMonth(), i + 1)
      ),
    };
  }, [sprint?.startDate, sprint?.endDate, viewMode]);

  const { users, unassignedUnscheduledTasks } = useMemo(() => {
    const bugsData = bugsDataQuery?.data?.decryptedData || {};

    // 1. Get base data from API
    const rawResult = buildUsersFromNewData(
      bugsData,
      sprint,
      viewStart,
      viewEnd,
      viewMode,
      viewDays
    );

    if (Object.keys(optimisticUpdates).length === 0) return rawResult;

    const processedUsers = [...rawResult.users];
    let processedUnassigned = [...rawResult.unassignedUnscheduledTasks];

    Object.entries(optimisticUpdates).forEach(([taskId, updates]) => {
      const unassignedIndex = processedUnassigned.findIndex(
        (t) => t.id === taskId
      );
      let task = null;
      let source = "unassigned";

      if (unassignedIndex !== -1) {
        task = { ...processedUnassigned[unassignedIndex] };
        processedUnassigned.splice(unassignedIndex, 1);
      } else {
        for (const user of processedUsers) {
          const taskIndex = user.tasks.findIndex((t: any) => t.id === taskId);
          if (taskIndex !== -1) {
            task = { ...user.tasks[taskIndex] };
            source = "assigned";
            break;
          }
        }
      }

      if (task) {
        // Apply the overrides
        if (updates.summary) task.name = updates.summary; // Map summary to name
        if (updates.severity) task.severity = updates.severity;
        if (updates.bugStatus) task.status = updates.bugStatus;
        if (updates.dueDate) task.dueDate = updates.dueDate;

        // Handle Assignee specifically (taking the first one if array)
        if (updates.assignTo) {
          task.assignee = Array.isArray(updates.assignTo)
            ? updates.assignTo[0]
            : updates.assignTo;
        }

        // Re-evaluate: Where should this task go?
        const hasAssignee = task.assignee && task.assignee._id;
        const hasDueDate = task.dueDate && task.dueDate !== "No Due Date";

        if (hasAssignee && hasDueDate) {
          // MOVE TO USER GRID
          const assigneeId = task.assignee._id;
          let userRow = processedUsers.find((u) => u.id === assigneeId);

          // Create user row if it doesn't exist (e.g. newly assigned user)
          if (!userRow) {
            userRow = {
              id: assigneeId,
              name: task.assignee.fullName || task.assignee.email,
              email: task.assignee.email,
              avatar: task.assignee.avatar,
              tasks: [],
              tasksByDate: {},
            };
            viewDays.forEach((day) => {
              userRow.tasksByDate[day.toISOString().split("T")[0]] = [];
            });
            processedUsers.push(userRow);
          }

          // Add to user's task list if it wasn't there already
          const taskDate = new Date(task.dueDate);
          const dateKey = taskDate.toISOString().split("T")[0];

          // Simple check to prevent duplicates if source was already this user
          const exists = userRow.tasks.some((t: any) => t.id === task.id);
          if (!exists) {
            userRow.tasks.push(task);
            if (userRow.tasksByDate[dateKey]) {
              userRow.tasksByDate[dateKey].push(task);
            }
          }
        } else {
          // MOVE TO UNASSIGNED
          processedUnassigned.push(task);
        }
      }
    });

    return {
      users: processedUsers,
      unassignedUnscheduledTasks: processedUnassigned,
    };
  }, [
    bugsDataQuery,
    sprint,
    viewStart,
    viewEnd,
    viewMode,
    viewDays,
    optimisticUpdates,
  ]);

  const leftColWidth = viewMode === "month" ? 180 : 290;

  const availableStatus = statusLists?.data?.decryptedData?.items || [];
  const users_data = companyMemberDataQuery?.data?.decryptedData || [];

  const severityOptions = [
    { value: "Highest", label: t("Highest"), icon: <HighestIcon /> },
    { value: "High", label: t("High"), icon: <HighIcon /> },
    { value: "Medium", label: t("Medium"), icon: <MediumIcon /> },
    { value: "Low", label: t("Low"), icon: <LowIcon /> },
  ];

  useEffect(() => {
    if (editingNameId && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [editingNameId]);

  const handleNameClick = (event, taskId, currentName) => {
    event.stopPropagation();
    setEditingNameId(taskId);
    setEditedName(currentName);
  };

  const handleNameChange = (event) => {
    setEditedName(event.target.value);
  };

  const saveEditedName = async () => {
    if (!editedName.trim()) {
      toast.error(t("Name cannot be empty"));
      return;
    }
    setOptimisticUpdates((prev) => ({
      ...prev,
      [editingNameId]: { ...prev[editingNameId], summary: editedName.trim() },
    }));
    try {
      const task = unassignedUnscheduledTasks.find(
        (task) => task.id === editingNameId
      );
      if (!task) {
        toast.error(t("Task not found"));
        return;
      }

      const { data } = await fetchBugDetail({
        bugId: task.id,
        withDetails: true,
      });
      const bugData = data?.data?.decryptedData || task;

      // Handle new assignTo structure - it's now a single object, not an array
      const assignToIds = bugData.assignTo
        ? [bugData.assignTo._id]
        : task.assignee
        ? [task.assignee._id]
        : [];

      const updatePayload = {
        bugId: task.id,
        summary: editedName.trim(),
        description: bugData.description,
        severity: bugData.severity,
        assignTo: assignToIds,
        pageUrl: bugData.pageUrl,
        // updatedAt: new Date(),
        bugStatus: bugData.bugStatus?._id,
        screenShots: bugData.screenShots,
        attachments: bugData.attachments,
        tags: bugData.tags,
        // createdAt: bugData.createdAt,
        type: bugData.type,
        platform: bugData.platform,
        jiraConfig: {
          projectKey: bugData.jiraConfig?.projectKey,
          projectId: bugData?.project?._id || bugData.project,
          projectName: bugData.projectName,
          jiraDatas: bugData.jiraConfig?.jiraDatas,
        },
        dueDate: bugData.dueDate,
        estimation: bugData.estimation,
        project: bugData.project?._id,
      };

      await updateBug(updatePayload)
        .unwrap()
        .then(() => {
          toast.success(t("Task name updated successfully"));
          refetchBugs();
        })
        .catch((error) => {
          console.error("Update error:", error);
          toast.error(
            t("Failed to update task:", {
              error: error?.data?.message || t("Unknown error"),
            })
          );
        });
    } catch (error) {
      console.error("Error updating task name:", error);
      toast.error(t("An unexpected error occurred"));
    } finally {
      setEditingNameId(null);
    }
  };
  const handleNameKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      saveEditedName();
    } else if (event.key === "Escape") {
      event.preventDefault();
      setEditingNameId(null);
    }
  };

  const handleClickEdit = async (event, taskId, field) => {
    event.stopPropagation();
    setEditingTaskId(taskId);
    setEditingField(field);
    setAnchorEl(event.currentTarget);

    if (
      field === "assignTo" ||
      field === "dueDate" ||
      field === "bugStatus" ||
      field === "severity" ||
      field === "type"
    ) {
      try {
        const { data } = await fetchBugDetail({
          bugId: taskId,
          withDetails: true,
        });
        const task = data?.data?.decryptedData;

        // Handle new assignTo structure - single object instead of array
        if (task && task.assignTo) {
          const assignee = users_data.find(
            (user) => user._id === task.assignTo._id
          );
          setSelectedAssignees(assignee ? [assignee] : []);
        } else {
          setSelectedAssignees([]);
        }
      } catch (error) {
        console.error("Error fetching task details:", error);
      }
    }
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setEditingTaskId(null);
    setEditingField(null);
  };

  const handleAssigneeChange = (newAssignees) => {
    setSelectedAssignees(newAssignees);
    if (editingTaskId) {
      const assigneeIds = newAssignees.map((user) => user._id);
      handleUpdateField(assigneeIds);
    }
  };

  const handleUpdateField = async (value) => {
    try {
      const task = unassignedUnscheduledTasks.find(
        (task) => task.id === editingTaskId
      );

      if (!task) {
        toast.error(t("Task not found"));
        handleClosePopover();
        return;
      }
      const optimisticValue =
        editingField === "assignTo" ? selectedAssignees : value;

      setOptimisticUpdates((prev) => ({
        ...prev,
        [editingTaskId]: {
          ...prev[editingTaskId],
          [editingField]: optimisticValue,
        },
      }));
      const bugData = editingBugDetail?.data?.decryptedData || task;

      // Handle new assignTo structure - convert to array of IDs
      const assignToIds = bugData.assignTo
        ? [bugData.assignTo._id]
        : task.assignee
        ? [task.assignee._id]
        : [];

      let updatePayload = {
        bugId: task.id,
        summary: bugData.summary,
        description: bugData.description,
        severity: bugData.severity,
        assignTo: assignToIds,
        pageUrl: bugData.pageUrl,
        updatedAt: new Date(),
        bugStatus: bugData.bugStatus?._id,
        screenShots: bugData.screenShots,
        attachments: bugData.attachments,
        tags: bugData.tags,
        createdAt: bugData.createdAt,
        type: bugData.type,
        platform: bugData.platform,
        jiraConfig: {
          projectKey: bugData.jiraConfig?.projectKey,
          projectId: bugData.project?._id || bugData.project,
          projectName: bugData.projectName,
          jiraDatas: bugData.jiraConfig?.jiraDatas,
        },
        dueDate: bugData.dueDate,
        estimation: bugData.estimation,
        project: bugData.project?._id,
      };

      switch (editingField) {
        case "severity":
          updatePayload.severity = value;
          break;
        case "bugStatus":
          updatePayload.bugStatus = value;
          break;
        case "dueDate":
          updatePayload.dueDate = value;
          break;
        case "assignTo":
          updatePayload.assignTo = value;
          break;
        case "type":
          updatePayload.type = value;
          break;
        default:
          break;
      }

      await updateBug(updatePayload)
        .unwrap()
        .then(() => {
          toast.success(
            `${
              editingField === "bugStatus"
                ? "Status"
                : editingField === "severity"
                ? "Priority"
                : editingField === "assignTo"
                ? "Assignee"
                : editingField === "type"
                ? "Type"
                : "Due Date"
            } ${t("updated successfully")}`
          );
          // Refetch bugs to update the calendar view immediately
          refetchBugs();
        })
        .catch((error) => {
          console.error("Update error:", error);
          toast.error(
            `Failed to update ${
              editingField === "bugStatus"
                ? "status"
                : editingField === "severity"
                ? "priority"
                : editingField === "assignTo"
                ? "assignee"
                : editingField === "type"
                ? "type"
                : "due date"
            }: ${error?.data?.message || "Unknown error"}`
          );
        });
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error(t("An unexpected error occurred"));
    } finally {
      if (editingField !== "assignTo") {
        handleClosePopover();
      }
    }
  };

  const handleAssigneeBlur = () => {};

  const handleTaskClick = (taskId: string) => {
    setSelectedTaskId(taskId);
    setIsModalOpen(true);
  };

  const columns = createColumns(
    editingNameId,
    editedName,
    nameInputRef,
    handleNameClick,
    handleNameChange,
    saveEditedName,
    handleNameKeyDown,
    handleClickEdit,
    dateFormat,
    handleTaskClick
  );

  if (isSprintLoading || isBugsLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          marginTop: 4,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!sprintData?.data?.decryptedData && !sprint) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          marginTop: 4,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          bgcolor: "#fff",
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <SprintHeader
            sprint={sprint}
            totalTasks={bugsDataQuery?.data?.decryptedData?.total || 0}
            viewMode={viewMode}
            setViewMode={setViewMode}
            zoom={zoom}
            setZoom={setZoom}
            anchor={anchor}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <WorkloadGrid
            users={users}
            viewMode={viewMode}
            viewDays={viewDays}
            leftColWidth={leftColWidth}
            zoomed={zoomed}
            onTaskClick={handleTaskClick}
            onDateHeaderClick={handleDateHeaderClick}
          />

          {users.length === 0 && unassignedUnscheduledTasks.length === 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 300,
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography color="text.secondary" variant="h6">
                No tasks found in this sprint
              </Typography>
              <Typography color="text.secondary" variant="body2">
                Tasks will appear here once they are added to the sprint
              </Typography>
            </Box>
          )}
          <UnassignedTasksSection
            tasks={unassignedUnscheduledTasks}
            columns={columns}
            isLoading={updatingBug || isFetchingEditingBug}
            hasUsers={users.length > 0}
          />
        </Box>

        <EditPopover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClosePopover}
          fieldType={editingField}
          editingTask={unassignedUnscheduledTasks.find(
            (task) => task.id === editingTaskId
          )}
          onUpdate={handleUpdateField}
          theme={theme}
          t={t}
          severityOptions={severityOptions}
          statusOptions={availableStatus}
          isLoadingStatusLabel={isLoadingStatusLabel}
          users={users_data}
          selectedAssignees={selectedAssignees}
          handleAssigneeChange={handleAssigneeChange}
          handleAssigneeBlur={handleAssigneeBlur}
          isUpdating={updatingBug}
          typeOptions={boardState?.projectDetails?.itemTypes || []}
        />
      </Box>
      <TaskUpdateDrawer
        open={isModalOpen}
        onClose={handleClose}
        setBugId={() => {}}
        setDrawerOpen={setIsModalOpen}
        bugId={selectedTaskId || ""}
        users={users_data || []}
        createdBy={""}
        setCreatedBy={() => {}}
        refreshColumn={refetchBugs}
      />
      <WorkloadSummaryDrawer
        open={summaryDrawerOpen}
        onClose={() => setSummaryDrawerOpen(false)}
        selectedDate={selectedDateForDrawer}
        sprint={sprintData?.data?.decryptedData}
        parentData={parentData}
      />
    </>
  );
};

export default SprintWorkload;
